/**
 * Hooks de données. Tout le server state passe par TanStack Query.
 * Aucun `useEffect` + `useState` pour charger de la donnée.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { endpoints } from './endpoints'
import type {
  CloseShiftInput,
  CustomerInput,
  DeliveryInput,
  EquipmentInput,
  FuelProductInput,
  InvoiceInput,
  InvoicePaymentInput,
  NozzleInput,
  NozzleUpdateInput,
  MaintenanceRecordInput,
  OpenShiftInput,
  PumpInput,
  PumpUpdateInput,
  ReportQuery,
  ShopArticleInput,
  ShopInventoryInput,
  ShopMovementInput,
  ShopSaleInput,
  PurchaseOrderInput,
  RoleInput,
  SiteInspectionInput,
  StationInput,
  TankGaugingInput,
  TankInput,
  TankUpdateInput,
  UserInput,
  VoucherBatchInput,
} from './types'

/** Vues de supervision : rafraîchies périodiquement, sans WebSocket. */
const LIVE = { refetchInterval: 45_000 } as const
/** Référentiels quasi statiques. */
const STATIC = { staleTime: 5 * 60_000 } as const

/** Une seule page suffisamment large pour les écrans de synthèse. */
const WIDE = { per_page: 100 }

export const keys = {
  me: ['me'] as const,
  modules: ['modules'] as const,
  users: ['users'] as const,
  roles: ['roles'] as const,
  permissions: ['permissions'] as const,
  stations: ['stations'] as const,
  station: (id: number) => ['stations', id] as const,
  products: ['fuel-products'] as const,
  tanks: ['tanks'] as const,
  pumps: ['pumps'] as const,
  tank: (id: number) => ['tanks', id] as const,
  shifts: ['work-shifts'] as const,
  shift: (id: number) => ['work-shifts', id] as const,
  suppliers: ['suppliers'] as const,
  orders: ['purchase-orders'] as const,
  deliveries: ['deliveries'] as const,
  customers: ['customers'] as const,
  customer: (id: number) => ['customers', id] as const,
  invoices: ['invoices'] as const,
  payments: (invoiceId: number) => ['invoices', invoiceId, 'payments'] as const,
  vouchers: (filters: Record<string, unknown> = {}) => ['vouchers', filters] as const,
  voucherBatches: ['voucher-batches'] as const,
  shopArticles: ['shop', 'articles'] as const,
  shopStocks: (filters: Record<string, unknown> = {}) => ['shop', 'stocks', filters] as const,
  shopSales: (filters: Record<string, unknown> = {}) => ['shop', 'sales', filters] as const,
  shopInventories: ['shop', 'inventories'] as const,
  report: (kind: string, q: Record<string, unknown>) => ['reports', kind, q] as const,
  voucherBatch: (id: number) => ['voucher-batches', id] as const,
  anomalies: ['anomalies'] as const,
  anomaly: (id: number) => ['anomalies', id] as const,
  alerts: ['alerts'] as const,
  equipment: ['equipment'] as const,
  records: ['maintenance-records'] as const,
  inspections: ['site-inspections'] as const,
  audit: (filters: Record<string, unknown>) => ['audit-logs', filters] as const,
}

/* ── Lectures ────────────────────────────────────────────────── */

export const useMe = (enabled: boolean) =>
  useQuery({ queryKey: keys.me, queryFn: endpoints.auth.me, enabled, ...STATIC })
export const useModules = (enabled: boolean) =>
  useQuery({ queryKey: keys.modules, queryFn: endpoints.modules.list, enabled, ...STATIC })
/** Rôles et catalogue : quasi statiques, mais invalidés dès qu'un rôle change. */
export const useRoles = (enabled = true) =>
  useQuery({ queryKey: keys.roles, queryFn: endpoints.roles.list, enabled, ...STATIC })
export const usePermissionCatalogue = (enabled = true) =>
  useQuery({ queryKey: keys.permissions, queryFn: endpoints.permissions.list, enabled, ...STATIC })

export const useUsers = () =>
  useQuery({ queryKey: keys.users, queryFn: () => endpoints.users.list(WIDE) })

export const useStations = () =>
  useQuery({ queryKey: keys.stations, queryFn: () => endpoints.stations.list(WIDE), ...STATIC })
export const useStation = (id: number) =>
  useQuery({
    queryKey: keys.station(id),
    queryFn: () => endpoints.stations.get(id),
    enabled: id > 0,
  })

export const useFuelProducts = () =>
  useQuery({ queryKey: keys.products, queryFn: endpoints.inventory.products, ...STATIC })
export const useTanks = () =>
  useQuery({ queryKey: keys.tanks, queryFn: () => endpoints.inventory.tanks(WIDE), ...LIVE })
export const useTank = (id: number) =>
  useQuery({
    queryKey: keys.tank(id),
    queryFn: () => endpoints.inventory.tank(id),
    enabled: id > 0,
  })

export const useShifts = () =>
  useQuery({ queryKey: keys.shifts, queryFn: () => endpoints.shifts.list(WIDE), ...LIVE })
export const useShift = (id: number) =>
  useQuery({ queryKey: keys.shift(id), queryFn: () => endpoints.shifts.get(id), enabled: id > 0 })

export const useSuppliers = () =>
  useQuery({ queryKey: keys.suppliers, queryFn: endpoints.procurement.suppliers, ...STATIC })
export const useOrders = () =>
  useQuery({ queryKey: keys.orders, queryFn: () => endpoints.procurement.orders(WIDE) })
export const useDeliveries = () =>
  useQuery({ queryKey: keys.deliveries, queryFn: () => endpoints.procurement.deliveries(WIDE) })

export const useCustomers = () =>
  useQuery({ queryKey: keys.customers, queryFn: () => endpoints.customers.list(WIDE) })
export const useCustomer = (id: number) =>
  useQuery({
    queryKey: keys.customer(id),
    queryFn: () => endpoints.customers.get(id),
    enabled: id > 0,
  })
export const useInvoices = () =>
  useQuery({ queryKey: keys.invoices, queryFn: () => endpoints.customers.invoices(WIDE) })

export const useAnomalies = (enabled = true) =>
  useQuery({
    queryKey: keys.anomalies,
    queryFn: () => endpoints.risk.anomalies(WIDE),
    enabled,
    ...LIVE,
  })
export const useAnomaly = (id: number) =>
  useQuery({
    queryKey: keys.anomaly(id),
    queryFn: () => endpoints.risk.anomaly(id),
    enabled: id > 0,
  })
export const useAlerts = (enabled = true) =>
  useQuery({ queryKey: keys.alerts, queryFn: () => endpoints.risk.alerts(WIDE), enabled, ...LIVE })

export const useEquipment = () =>
  useQuery({ queryKey: keys.equipment, queryFn: () => endpoints.maintenance.equipment(WIDE) })
export const useMaintenanceRecords = () =>
  useQuery({ queryKey: keys.records, queryFn: () => endpoints.maintenance.records(WIDE) })
export const useInspections = () =>
  useQuery({ queryKey: keys.inspections, queryFn: () => endpoints.inspections.list(WIDE) })
export const useAuditLogs = (filters: { event?: string; user_id?: number } = {}) =>
  useQuery({
    queryKey: keys.audit(filters),
    queryFn: () => endpoints.audit.list({ ...WIDE, ...filters }),
  })

/* ── Écritures ───────────────────────────────────────────────── */

function invalidate(...groups: readonly (readonly unknown[])[]) {
  return (qc: ReturnType<typeof useQueryClient>) =>
    Promise.all(groups.map((k) => qc.invalidateQueries({ queryKey: k })))
}

export function useUpdateModule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      endpoints.modules.update(key, enabled),
    onSuccess: () => invalidate(keys.modules)(qc),
  })
}

export function useSaveRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: RoleInput }) =>
      id ? endpoints.roles.update(id, input) : endpoints.roles.create(input),
    // Recomposer un rôle change les droits de ses titulaires : le profil
    // courant et la liste des comptes doivent être relus, pas seulement les rôles.
    onSuccess: () => invalidate(keys.roles, keys.users, keys.me)(qc),
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => endpoints.roles.remove(id),
    onSuccess: () => invalidate(keys.roles)(qc),
  })
}

export function useSaveUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: UserInput }) =>
      id ? endpoints.users.update(id, input) : endpoints.users.create(input),
    onSuccess: () => invalidate(keys.users)(qc),
  })
}

export function useSaveStation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: StationInput }) =>
      id ? endpoints.stations.update(id, input) : endpoints.stations.create(input),
    onSuccess: () => invalidate(keys.stations, keys.me)(qc),
  })
}

export function useArchiveStation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => endpoints.stations.archive(id),
    onSuccess: () => invalidate(keys.stations)(qc),
  })
}

/* ── Matériel d'une station et tarifs ────────────────────────── */

export const usePumps = () =>
  useQuery({ queryKey: keys.pumps, queryFn: () => endpoints.inventory.pumps(WIDE) })

/**
 * Toute écriture sur le matériel invalide pompes et cuves à la fois : un
 * pistolet appartient à une pompe et tire sur une cuve, et n'afficher qu'une
 * moitié à jour donnerait une station incohérente à l'écran.
 */
const assetsChanged = (qc: ReturnType<typeof useQueryClient>) =>
  invalidate(keys.pumps, keys.tanks)(qc)

export function useSavePump() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: PumpInput | PumpUpdateInput }) =>
      id
        ? endpoints.inventory.updatePump(id, input as PumpUpdateInput)
        : endpoints.inventory.createPump(input as PumpInput),
    onSuccess: () => assetsChanged(qc),
  })
}

export function useArchivePump() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => endpoints.inventory.archivePump(id),
    onSuccess: () => assetsChanged(qc),
  })
}

export function useSaveNozzle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: NozzleInput | NozzleUpdateInput }) =>
      id
        ? endpoints.inventory.updateNozzle(id, input as NozzleUpdateInput)
        : endpoints.inventory.createNozzle(input as NozzleInput),
    onSuccess: () => assetsChanged(qc),
  })
}

export function useArchiveNozzle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => endpoints.inventory.archiveNozzle(id),
    onSuccess: () => assetsChanged(qc),
  })
}

export function useSaveTank() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: TankInput | TankUpdateInput }) =>
      id
        ? endpoints.inventory.updateTank(id, input as TankUpdateInput)
        : endpoints.inventory.createTank(input as TankInput),
    onSuccess: () => assetsChanged(qc),
  })
}

export function useArchiveTank() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => endpoints.inventory.archiveTank(id),
    onSuccess: () => assetsChanged(qc),
  })
}

/**
 * Un prix décide de ce que chaque poste ouvert ensuite devra encaisser : les
 * postes en cours gardent celui figé à leur ouverture, mais les listes qui
 * l'affichent doivent être relues.
 */
export function useSaveFuelProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: FuelProductInput }) =>
      id
        ? endpoints.inventory.updateProduct(id, input)
        : endpoints.inventory.createProduct(input),
    onSuccess: () => invalidate(keys.products, keys.tanks, keys.pumps)(qc),
  })
}

/* ── Règlements clients ──────────────────────────────────────── */

export const usePayments = (invoiceId: number) =>
  useQuery({
    queryKey: keys.payments(invoiceId),
    queryFn: () => endpoints.customers.payments(invoiceId),
    enabled: invoiceId > 0,
  })

export function useRecordPayment(invoiceId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: InvoicePaymentInput) =>
      endpoints.customers.recordPayment(invoiceId, input),
    // Un règlement change le solde de la facture et l'encours du client :
    // laisser l'un des deux périmé afficherait une dette déjà réglée.
    onSuccess: () => invalidate(keys.payments(invoiceId), keys.invoices, keys.customers)(qc),
  })
}

/* ── Boutique ────────────────────────────────────────────────── */

export const useShopArticles = (enabled = true) =>
  useQuery({
    queryKey: keys.shopArticles,
    queryFn: () => endpoints.shop.articles(WIDE),
    enabled,
    ...STATIC,
  })
export const useShopStocks = (filters: { station_id?: number; below_minimum?: boolean } = {}) =>
  useQuery({
    queryKey: keys.shopStocks(filters),
    queryFn: () => endpoints.shop.stocks({ ...WIDE, ...filters }),
  })
export const useShopSales = (filters: { station_id?: number } = {}) =>
  useQuery({
    queryKey: keys.shopSales(filters),
    queryFn: () => endpoints.shop.sales({ per_page: 50, ...filters }),
  })
export const useShopInventories = () =>
  useQuery({ queryKey: keys.shopInventories, queryFn: () => endpoints.shop.inventories(WIDE) })

/**
 * Toute écriture boutique invalide stock, ventes et comptages ensemble : le
 * stock découle des mouvements, et n'en rafraîchir qu'une partie afficherait
 * une boutique dont les chiffres se contredisent.
 */
const shopChanged = (qc: ReturnType<typeof useQueryClient>) =>
  invalidate(keys.shopStocks(), keys.shopSales(), keys.shopInventories, keys.shopArticles)(qc)

export function useSaveShopArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: ShopArticleInput }) =>
      id ? endpoints.shop.updateArticle(id, input) : endpoints.shop.createArticle(input),
    onSuccess: () => shopChanged(qc),
  })
}

export function useRecordShopMovement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ShopMovementInput) => endpoints.shop.createMovement(input),
    onSuccess: () => shopChanged(qc),
  })
}

export function useRecordShopSale() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ShopSaleInput) => endpoints.shop.createSale(input),
    onSuccess: () => shopChanged(qc),
  })
}

export function useRecordShopInventory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ShopInventoryInput) => endpoints.shop.createInventory(input),
    onSuccess: () => shopChanged(qc),
  })
}

/* ── Rapports ────────────────────────────────────────────────── */

/**
 * Totaux sommés par le serveur.
 *
 * Aucun de ces chiffres n'est recalculé dans le navigateur : sur un réseau
 * qui dépasse une page de résultats, un total local serait celui d'un
 * échantillon — faux, et faux sans que rien ne le signale.
 */
export const useReportOverview = (q: ReportQuery = {}, enabled = true) =>
  useQuery({
    queryKey: keys.report('overview', q),
    queryFn: () => endpoints.reports.overview(q),
    enabled,
    ...LIVE,
  })
export const useReportDaily = (q: ReportQuery = {}, enabled = true) =>
  useQuery({ queryKey: keys.report('daily', q), queryFn: () => endpoints.reports.daily(q), enabled })
export const useReportStations = (q: ReportQuery = {}, enabled = true) =>
  useQuery({
    queryKey: keys.report('stations', q),
    queryFn: () => endpoints.reports.stations(q),
    enabled,
  })
export const useReportAgents = (q: ReportQuery = {}, enabled = true) =>
  useQuery({
    queryKey: keys.report('agents', q),
    queryFn: () => endpoints.reports.agents(q),
    enabled,
  })

/* ── Bons carburant ──────────────────────────────────────────── */

export const useVouchers = (filters: Record<string, unknown> = {}) =>
  useQuery({
    queryKey: keys.vouchers(filters),
    queryFn: () => endpoints.vouchers.list({ ...WIDE, ...filters }),
  })
export const useVoucherBatches = () =>
  useQuery({ queryKey: keys.voucherBatches, queryFn: () => endpoints.vouchers.batches(WIDE) })
export const useVoucherBatch = (id: number) =>
  useQuery({
    queryKey: keys.voucherBatch(id),
    queryFn: () => endpoints.vouchers.batch(id),
    enabled: id > 0,
  })

export function useIssueVoucherBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: VoucherBatchInput) => endpoints.vouchers.createBatch(input),
    onSuccess: () => invalidate(keys.voucherBatches, keys.vouchers())(qc),
  })
}

export function useCancelVoucher() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ code, reason }: { code: string; reason: string }) =>
      endpoints.vouchers.cancel(code, reason),
    onSuccess: () => invalidate(keys.vouchers(), keys.voucherBatches)(qc),
  })
}

export function useGaugeTank() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: TankGaugingInput) => endpoints.inventory.gauge(input),
    onSuccess: (_, input) => invalidate(keys.tanks, keys.tank(input.tank_id))(qc),
  })
}

export function useOpenShift() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: OpenShiftInput) => endpoints.shifts.open(input),
    onSuccess: () => invalidate(keys.shifts)(qc),
  })
}

export function useCloseShift() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: CloseShiftInput }) =>
      endpoints.shifts.close(id, input),
    onSuccess: (_, { id }) => invalidate(keys.shifts, keys.shift(id))(qc),
  })
}

export function useApproveShift() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => endpoints.shifts.approve(id),
    onSuccess: (_, id) => invalidate(keys.shifts, keys.shift(id))(qc),
  })
}

export function useCreateOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PurchaseOrderInput) => endpoints.procurement.createOrder(input),
    onSuccess: () => invalidate(keys.orders)(qc),
  })
}

export function useCreateDelivery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: DeliveryInput) => endpoints.procurement.createDelivery(input),
    onSuccess: () => invalidate(keys.deliveries, keys.tanks)(qc),
  })
}

export function useSaveCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: CustomerInput }) =>
      id ? endpoints.customers.update(id, input) : endpoints.customers.create(input),
    onSuccess: () => invalidate(keys.customers)(qc),
  })
}

export function useCreateInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: InvoiceInput) => endpoints.customers.createInvoice(input),
    onSuccess: (_, input) =>
      invalidate(keys.invoices, keys.customers, keys.customer(input.customer_id))(qc),
  })
}

export function useResolveAnomaly() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, resolution }: { id: number; resolution: string }) =>
      endpoints.risk.resolve(id, resolution),
    onSuccess: (_, { id }) => invalidate(keys.anomalies, keys.anomaly(id))(qc),
  })
}

export function useMarkAlertRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => endpoints.risk.markRead(id),
    onSuccess: () => invalidate(keys.alerts)(qc),
  })
}

export function useCreateEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: EquipmentInput) => endpoints.maintenance.createEquipment(input),
    onSuccess: () => invalidate(keys.equipment)(qc),
  })
}

export function useCreateMaintenanceRecord() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: MaintenanceRecordInput) => endpoints.maintenance.createRecord(input),
    onSuccess: () => invalidate(keys.records)(qc),
  })
}

export function useCreateInspection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SiteInspectionInput) => endpoints.inspections.create(input),
    onSuccess: () => invalidate(keys.inspections)(qc),
  })
}
